// src/pages/Suppliers.jsx
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "components/ui/Button";
import { listSuppliers, deleteSupplier } from "api/suppliers";
import { SupplierModal } from "components/suppliers/SupplierModal";
import { debounce } from "lodash";

export default function SuppliersPage() {
    const [suppliers, setSuppliers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [showModal, setShowModal] = useState(false);
    const [editingSupplier, setEditingSupplier] = useState(null);
    const [viewMode, setViewMode] = useState("grid"); // 'grid' or 'list'

    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [count, setCount] = useState(0);

    const totalPages = Math.max(1, Math.ceil(count / pageSize));

    const fetchSuppliers = useCallback(async (q = "", currentPage = 1, currentLimit = 20) => {
        try {
            setLoading(true);
            const { rows, count: totalCount } = await listSuppliers({
                q,
                limit: currentLimit,
                offset: (currentPage - 1) * currentLimit
            });

            setSuppliers(Array.isArray(rows) ? rows : []);
            setCount(totalCount || 0);
        } catch (e) {
            console.error("Failed to fetch suppliers:", e);
            setSuppliers([]);
            setCount(0);
        } finally {
            setLoading(false);
        }
    }, []);

    const debouncedFetch = useCallback(
        debounce((q, p, s) => fetchSuppliers(q, p, s), 300),
        [fetchSuppliers]
    );

    useEffect(() => {
        fetchSuppliers(search, page, pageSize);
    }, [page, pageSize]); // Fetch on page/size change

    useEffect(() => {
        setPage(1); // Reset to page 1 on search
        debouncedFetch(search, 1, pageSize);
    }, [search, debouncedFetch, pageSize]);

    const handleCreate = () => {
        setEditingSupplier(null);
        setShowModal(true);
    };

    const handleEdit = (supplier) => {
        setEditingSupplier(supplier);
        setShowModal(true);
    };

    const handleDelete = async (supplier) => {
        if (!window.confirm(`"${supplier.name}" yetkazib beruvchisini o'chirmoqchimisiz?`)) return;
        try {
            await deleteSupplier(supplier.id);
            // Re-fetch current page
            fetchSuppliers(search, page, pageSize);
        } catch (e) {
            alert("Xatolik: " + e.message);
        }
    };

    return (
        <div className="p-4 sm:p-6 lg:p-8 bg-gray-50/50 dark:bg-slate-900 min-h-screen transition-colors">
            <div className="max-w-screen-xl mx-auto space-y-6">
                <header className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-gray-900 dark:text-slate-100">
                            Yetkazib beruvchilar
                        </h1>
                        <p className="mt-1 text-md text-gray-600 dark:text-slate-400">
                            Yetkazib beruvchilar ro'yxatini boshqarish
                        </p>
                    </div>
                    <button
                        onClick={handleCreate}
                        className="w-full sm:w-auto px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg shadow-sm transition-colors flex items-center justify-center gap-2"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Qo'shish
                    </button>
                </header>

                {/* Search and Controls */}
                <div className="flex flex-col md:flex-row items-center gap-4 flex-wrap">
                    <div className="relative w-full md:flex-1 md:min-w-[250px]">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <svg className="w-5 h-5 text-gray-400 dark:text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                        </div>
                        <input
                            type="text"
                            placeholder="Qidirish..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-700 rounded-lg shadow-sm text-gray-900 dark:text-slate-100 placeholder-gray-400 dark:placeholder-slate-500"
                        />
                    </div>

                    <div className="flex items-center gap-3">
                        {/* View Mode Toggle */}
                        <div className="flex items-center bg-gray-100 dark:bg-slate-700 rounded-lg p-1">
                            <button
                                onClick={() => setViewMode("grid")}
                                className={`px-3 py-1.5 rounded-md transition-all ${viewMode === "grid"
                                    ? "bg-white dark:bg-slate-600 text-blue-600 dark:text-blue-400 shadow-sm"
                                    : "text-gray-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-slate-200"
                                    }`}
                                title="Plitka"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                                </svg>
                            </button>
                            <button
                                onClick={() => setViewMode("list")}
                                className={`px-3 py-1.5 rounded-md transition-all ${viewMode === "list"
                                    ? "bg-white dark:bg-slate-600 text-blue-600 dark:text-blue-400 shadow-sm"
                                    : "text-gray-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-slate-200"
                                    }`}
                                title="Ro'yxat"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>

                {/* Suppliers List */}
                {loading ? (
                    <div className="flex justify-center py-12">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    </div>
                ) : suppliers.length === 0 ? (
                    <div className="text-center py-12 bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700">
                        <p className="text-gray-500 dark:text-slate-400">
                            {search ? "Hech narsa topilmadi" : "Yetkazib beruvchilar yo'q"}
                        </p>
                    </div>
                ) : (
                    <div className={viewMode === "grid"
                        ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
                        : "space-y-4"
                    }>
                        {suppliers.map((supplier) => (
                            <SupplierCard
                                key={supplier.id}
                                supplier={supplier}
                                onEdit={handleEdit}
                                onDelete={handleDelete}
                            />
                        ))}
                    </div>
                )}

                {/* Pagination */}
                {count > 0 && (
                    <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm">
                        <div className="flex items-center gap-4">
                            <span className="text-gray-600 dark:text-slate-400">
                                Jami: {count}
                            </span>
                            <select
                                value={pageSize}
                                onChange={(e) => {
                                    setPageSize(Number(e.target.value));
                                    setPage(1);
                                }}
                                className="bg-white dark:bg-slate-800 border-gray-300 dark:border-slate-700 rounded-md text-sm py-1 pl-2 pr-6 focus:ring-blue-500 focus:border-blue-500"
                            >
                                <option value={10}>10 / sahifa</option>
                                <option value={20}>20 / sahifa</option>
                                <option value={50}>50 / sahifa</option>
                                <option value={100}>100 / sahifa</option>
                            </select>
                        </div>

                        <div className="flex items-center gap-2">
                            <Button
                                variant="outline"
                                onClick={() => setPage(p => Math.max(1, p - 1))}
                                disabled={page === 1}
                                className="px-3"
                            >
                                Orqaga
                            </Button>
                            <span className="text-gray-900 dark:text-slate-200 font-medium">
                                Sahifa {page} / {totalPages}
                            </span>
                            <Button
                                variant="outline"
                                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                disabled={page === totalPages}
                                className="px-3"
                            >
                                Oldinga
                            </Button>
                        </div>
                    </div>
                )}
            </div>

            {showModal && (
                <SupplierModal
                    supplier={editingSupplier}
                    onClose={() => setShowModal(false)}
                    onSaved={() => {
                        setShowModal(false);
                        setSearch(''); // Clear search to show all
                        fetchSuppliers('', 1, pageSize); // Fetch all suppliers
                    }}
                />
            )}
        </div>
    );
}

const SupplierCard = ({ supplier, onEdit, onDelete }) => {
    const navigate = useNavigate();

    const handleCardClick = () => {
        navigate(`/suppliers/${supplier.id}`);
    };

    const handleEdit = (e) => {
        e.stopPropagation();
        onEdit(supplier);
    };

    const handleDelete = (e) => {
        e.stopPropagation();
        onDelete(supplier);
    };

    return (
        <div
            onClick={handleCardClick}
            className="group relative bg-white rounded-2xl p-5 border border-gray-200 hover:border-blue-200 hover:shadow-[0_4px_20px_-10px_rgba(37,99,235,0.1)] transition-all duration-300 cursor-pointer"
        >
            <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-4 flex-1 min-w-0">
                    <div className="h-12 w-12 rounded-xl bg-slate-50 text-slate-500 group-hover:bg-blue-50 group-hover:text-blue-600 flex items-center justify-center border border-slate-100 transition-colors shrink-0">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                        <h3 className="text-base font-bold text-gray-900 group-hover:text-blue-700 transition-colors truncate">
                            {supplier.name}
                        </h3>
                        <p className="text-xs text-gray-500 mt-0.5 font-mono">
                            ID: {supplier.id}
                        </p>
                    </div>
                </div>

                <div className="flex gap-1.5 ml-2">
                    <button
                        onClick={handleEdit}
                        className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Tahrirlash"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                    </button>
                    <button
                        onClick={handleDelete}
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="O'chirib tashlash"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                    </button>
                </div>
            </div>

            <div className="space-y-2">
                {supplier.phone && (
                    <div className="flex items-center gap-2 text-xs text-gray-600">
                        <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                        <a href={`tel:${supplier.phone}`} className="hover:text-blue-600 transition-colors font-medium" onClick={(e) => e.stopPropagation()}>
                            {supplier.phone}
                        </a>
                    </div>
                )}
                {supplier.email && (
                    <div className="flex items-center gap-2 text-xs text-gray-600">
                        <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        <a href={`mailto:${supplier.email}`} className="hover:text-blue-600 transition-colors truncate font-medium" onClick={(e) => e.stopPropagation()}>
                            {supplier.email}
                        </a>
                    </div>
                )}
                {supplier.address && (
                    <div className="flex items-start gap-2 text-xs text-gray-600">
                        <svg className="w-3.5 h-3.5 text-gray-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <span className="flex-1">
                            {supplier.address}
                        </span>
                    </div>
                )}
                {!supplier.phone && !supplier.email && !supplier.address && (
                    <p className="text-xs text-gray-400 italic">Kontakt ma'lumotlari yo'q</p>
                )}
            </div>
        </div>
    );
};


